import React, { useState, useEffect } from 'react';
import { Star, CheckCircle } from 'lucide-react';
import { productService } from '../../../services/productService';

const FilterSidebar = ({ filters, onFilterChange }) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const data = await productService?.getAllProducts();
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleRatingChange = (rating) => {
    onFilterChange({
      ...filters,
      rating: filters?.rating === rating ? null : rating
    });
  };

  const handleVerifiedChange = (value) => {
    onFilterChange({
      ...filters,
      isVerified: filters?.isVerified === value ? null : value
    });
  };

  const handleProductChange = (productId) => {
    onFilterChange({
      ...filters,
      productId: filters?.productId === productId ? null : productId
    });
  };

  const clearFilters = () => {
    onFilterChange({
      rating: null,
      isVerified: null,
      productId: null
    });
  };

  const hasActiveFilters = filters?.rating || filters?.isVerified !== null || filters?.productId;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-gray-900">Filters</h2>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Rating Filter */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Rating
        </h3>
        <div className="space-y-2">
          {[5, 4, 3, 2, 1]?.map((rating) => (
            <button
              key={rating}
              onClick={() => handleRatingChange(rating)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                filters?.rating === rating
                  ? 'bg-blue-50 border-2 border-blue-600' :'border-2 border-transparent hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5]?.map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= rating
                        ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600">& up</span>
            </button>
          ))}
        </div>
      </div>

      {/* Verification Filter */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Verification
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => handleVerifiedChange(true)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              filters?.isVerified === true
                ? 'bg-blue-50 border-2 border-blue-600' :'border-2 border-transparent hover:bg-gray-50'
            }`}
          >
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-700">Verified Only</span>
          </button>
          <button
            onClick={() => handleVerifiedChange(false)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              filters?.isVerified === false
                ? 'bg-blue-50 border-2 border-blue-600' :'border-2 border-transparent hover:bg-gray-50'
            }`}
          >
            <span className="text-sm text-gray-700">All Reviews</span>
          </button>
        </div>
      </div>

      {/* Product Filter */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Filter by Product
        </h3>
        {loadingProducts ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {products?.map((product) => (
              <button
                key={product?.id}
                onClick={() => handleProductChange(product?.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
                  filters?.productId === product?.id
                    ? 'bg-blue-50 border-2 border-blue-600' :'border-2 border-transparent hover:bg-gray-50'
                }`}
              >
                <img
                  src={product?.imageUrl}
                  alt={product?.imageAlt || `${product?.name} thumbnail`}
                  className="w-10 h-10 object-cover rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {product?.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {product?.brand}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSidebar;